import { queryClient } from "@/queryClient"
import { useOrganizationConfig } from "@/services/organizations/retrieveOrganizationConfig"
import {
  BASE_ORDERS_QUERY_KEY,
  BASE_TRANSACTIONS_FEED_QUERY_KEY,
} from "@/services/queryKeys"
import { retrieveSettings } from "@/services/settings/retrieveSettings"
import { Socket } from "phoenix"
import type { Channel } from "phoenix"

type BustableKey = "orders" | "settings"

const { serialNumber } = useDevice()
const { schemaName, setTenantState } = useTenant()

const socketRef = ref<Socket>()
const merchantChannel = ref<Channel>()

const initSocket = (token: string) => {
  socketRef.value = new Socket(__WS_ENDPOINT__, {
    params: {
      token,
    },
  })

  socketRef.value.connect()

  connectToMerchantChannel()
}

const closeSocket = () => {
  socketRef.value?.disconnect()
  socketRef.value = undefined
}

const connectToMerchantChannel = () => {
  if (!socketRef.value) return

  merchantChannel.value = socketRef.value.channel(
    `merchant:${schemaName.value}`,
    {}
  )

  merchantChannel.value?.join(1000)
  merchantChannel.value?.on("notify", ({ message }) => {
    console.info(message)
  })
  merchantChannel.value?.on(
    "bust",
    async ({
      key,
      exclude,
    }: {
      key: BustableKey
      exclude: string[]
    }) => {
      if (
        serialNumber.value &&
        exclude.includes(serialNumber.value)
      )
        return

      if (key === "settings") {
        const state = await retrieveSettings()
        setTenantState(state)
        return
      }

      if (key === "orders") {
        queryClient.invalidateQueries({
          queryKey: [BASE_ORDERS_QUERY_KEY],
        })
        queryClient.invalidateQueries({
          queryKey: [BASE_TRANSACTIONS_FEED_QUERY_KEY],
        })
        return
      }
    }
  )
}

/*
Call once in app lifecycle to setup and manage websocket connection
**/
export const useSocket = () => {
  const { accessToken, isLoggedIn } = useAuth()
  const { organizationConfig } = useOrganizationConfig()

  watch(
    [
      accessToken,
      isLoggedIn,
      organizationConfig,
      schemaName,
    ],
    ([
      newToken,
      newIsLoggedIn,
      newOrgConfig,
      newSchemaName,
    ]) => {
      closeSocket()
      if (
        newToken &&
        newIsLoggedIn &&
        newOrgConfig?.websocketServiceEnabled &&
        newSchemaName
      ) {
        initSocket(newToken)
      }
    },
    { immediate: true }
  )
}
